import { startLoading } from "./loadingTracker";
import toast from "react-hot-toast";
import {
   getService,
   postService,
   putService,
   patchService,
   deleteService,
} from "./services";

/**
 * Generic API caller — handles loading, success toast, error toast & redirection.
 * No Redux required. Caller only receives the resolved data (or null on failure).
 *
 * @param {Object}           options
 * @param {"GET"|"POST"|"PUT"|"PATCH"|"DELETE"} options.method
 *    HTTP method. Default: "GET"
 *
 * @param {string}           options.endpoint
 *    API endpoint path, e.g. "/user/list"
 *
 * @param {any}              [options.data]
 *    Request body — used for POST / PUT / PATCH.
 *
 * @param {boolean}          [options.auth]
 *    Attach Bearer token? Default: true.
 *
 * @param {Object}           [options.params]
 *    Query-string key/value pairs, e.g. { limit: 10, offset: 0 }.
 *    Automatically serialised and appended to the URL.
 *
 * @param {Function}         [options.setLoading]
 *    Optional setState setter from the caller — called with true/false
 *    to control a loading spinner.  e.g. setLoading={setIsLoading}
 *
 * @param {string|false}     [options.successMsg]
 *    Toast shown on success.
 *    Pass false to suppress the success toast entirely.
 *    Default: "Success!"
 *
 * @param {string|false}     [options.errorMsg]
 *    Override for the error toast.
 *    Pass false to suppress the error toast entirely.
 *    If omitted the server message (or a generic fallback) is used.
 *
 * @param {string}           [options.redirectTo]
 *    React-Router path to navigate to after a successful response.
 *    Requires passing the `navigate` function via options.navigate.
 *
 * @param {Function}         [options.navigate]
 *    The navigate function from useNavigate() in the calling component.
 *    Required only when redirectTo is set.
 *
 * @param {Function}         [options.onSuccess]
 *    Optional callback fired with the response data on success.
 *
 * @param {Function}         [options.onError]
 *    Optional callback fired with the error message on failure.
 *
 * @returns {Promise<any|null>}
 *    Resolves with the response data on success, or null on failure.
 *
 * ─── Usage Examples ──────────────────────────────────────────────────────────
 *
 * // 1. GET — fetch a list
 * const data = await apiCall({
 *   method: "GET",
 *   endpoint: "/user/list",
 *   setLoading: setIsLoading,
 * });
 *
 * // 2. GET — with query params
 * const data = await apiCall({
 *   method: "GET",
 *   endpoint: "/trip/list",
 *   params: { limit: 10, offset: 0, trip_status: "open" },
 *   setLoading: setIsLoading,
 * });
 *
 * // 3. POST — submit and redirect
 * const data = await apiCall({
 *   method: "POST",
 *   endpoint: "/user/add",
 *   data: formPayload,
 *   setLoading: setIsLoading,
 *   successMsg: "User added successfully!",
 *   redirectTo: RouteNames.userList,
 *   navigate,                 // ← from useNavigate()
 * });
 *
 * // 4. PATCH — update, no redirect
 * const data = await apiCall({
 *   method: "PATCH",
 *   endpoint: "/user/update",
 *   data: { id: 5, status: "active" },
 *   setLoading: setIsLoading,
 *   successMsg: "Status updated!",
 * });
 *
 * // 5. DELETE
 * const data = await apiCall({
 *   method: "DELETE",
 *   endpoint: `/user/delete/${id}`,
 *   setLoading: setIsLoading,
 *   successMsg: "Deleted successfully!",
 * });
 *
 * // 6. PUT — full update
 * const data = await apiCall({
 *   method: "PUT",
 *   endpoint: "/vehicle/update",
 *   data: fullPayload,
 *   setLoading: setIsLoading,
 *   successMsg: "Vehicle updated!",
 * });
 *
 * // 7. No auth (public route)
 * const data = await apiCall({
 *   method: "POST",
 *   endpoint: "/auth/login",
 *   data: { email, password },
 *   auth: false,
 *   successMsg: false,       // ← suppress toast
 * });
 *
 * // 8. onSuccess / onError callbacks
 * await apiCall({
 *   method: "GET",
 *   endpoint: "/role/list",
 *   setLoading: setIsLoading,
 *   onSuccess: (data) => dispatch(setRoles(data)),
 *   onError:   (msg)  => console.error(msg),
 * });
 */
const apiCall = async ({
   method = "GET",
   endpoint,
   data = null,
   auth = true,
   params = null,
   setLoading = null,
   successMsg = "Success!",
   errorMsg = undefined,
   redirectTo = null,
   navigate = null,
   onSuccess = null,
   onError = null,
}) => {
   // ── 1. Start loading ──────────────────────────────────────────────────
   const finishLoading = startLoading(setLoading);

   try {
      // ── 2. Build URL with optional query params ───────────────────────────
      let url = endpoint;
      if (params && Object.keys(params).length > 0) {
         const qs = new URLSearchParams(
            Object.fromEntries(
               Object.entries(params).filter(([, v]) => v !== undefined && v !== null),
            ),
         ).toString();
         if (qs) url += (url.includes("?") ? "&" : "?") + qs;
      }

      // ── 3. Fire the request ───────────────────────────────────────────────
      let response;

      switch (method.toUpperCase()) {
         case "GET":
            response = await getService(url, auth);
            break;
         case "POST":
            response = await postService(url, data, auth);
            break;
         case "PUT":
            response = await putService(url, data, auth);
            break;
         case "PATCH":
            response = await patchService(url, data, auth);
            break;
         case "DELETE":
            response = await deleteService(url, auth);
            break;
         default:
            throw new Error(`Unsupported HTTP method: "${method}"`);
      }

      const responseData = response?.data ?? response;

      // ── 4. Success toast ───────────────────────────────────────────────
      if (successMsg !== false) {
         toast.success(successMsg);
      }

      // ── 5. onSuccess callback ──────────────────────────────────────────
      if (typeof onSuccess === "function") {
         onSuccess(responseData);
      }

      // ── 6. Redirect ────────────────────────────────────────────────────
      if (redirectTo && typeof navigate === "function") {
         navigate(redirectTo);
      }

      return responseData;

   } catch (err) {
      const serverMessage =
         err?.response?.data?.message ||
         err?.response?.data?.error ||
         err?.message ||
         "Something went wrong. Please try again.";

      // ── 7. Error toast ─────────────────────────────────────────────────
      const displayError = errorMsg !== undefined ? errorMsg : serverMessage;
      if (displayError !== false) {
         const safeError =
            typeof displayError === "string"
               ? displayError
               : typeof displayError === "object" && displayError !== null
               ? displayError?.message || "Something went wrong. Please try again."
               : String(displayError ?? "Something went wrong. Please try again.");
         toast.error(safeError);
      }

      // ── 8. onError callback ────────────────────────────────────────────
      if (typeof onError === "function") {
         onError(serverMessage);
      }

      return null;

   } finally {
      // ── 9. Stop loading (always) ───────────────────────────────────────
      finishLoading();
   }
};

export default apiCall;
